/**
 * Registration review — the staff-facing half.
 *
 * Separate from `registration-requests.ts`, which holds the single public
 * submission call. The split mirrors the backend's `public.routes.ts` /
 * `internal.routes.ts` boundary: everything in this file reads or changes
 * submitted personal data and belongs behind the staff shell.
 */

import type {
  RegistrationAwaitingDecision,
  RegistrationRequest,
  RegistrationRequestListItem,
  Volunteer,
} from '@parinaam-vms/shared';
import { apiGet, apiGetPage, apiPost, toQueryString } from './client';
import type { Page } from './client';

/**
 * A `type`, not an `interface`, and that matters: TypeScript gives an object type
 * alias an implicit index signature and an interface none, so only the alias can
 * be passed to `toQueryString`. Every list query in this folder follows suit.
 */
export type RegistrationQueueQuery = {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
};

export function fetchRegistrations(
  params: RegistrationQueueQuery
): Promise<Page<RegistrationRequestListItem>> {
  return apiGetPage<RegistrationRequestListItem>(`/registration-requests${toQueryString(params)}`);
}

export function fetchRegistration(id: string): Promise<RegistrationRequest> {
  return apiGet<RegistrationRequest>(`/registration-requests/${id}`);
}

/**
 * Submissions still waiting on a decision — the panel at the top of the
 * volunteers screen (Plan D12).
 *
 * "Waiting" is decided by the *volunteer's* status, not the registration's, so
 * this list empties as staff work regardless of which screen they used.
 */
export function fetchAwaitingDecision(): Promise<RegistrationAwaitingDecision[]> {
  return apiGet<RegistrationAwaitingDecision[]>('/registration-requests/awaiting-decision');
}

/** What approval returns: both records, so the screen can name the volunteer it made. */
export interface ApprovalResult {
  request: RegistrationRequest;
  volunteer: Volunteer;
}

export function approveRegistration(id: string): Promise<ApprovalResult> {
  return apiPost<ApprovalResult>(`/registration-requests/${id}/approve`);
}

export function rejectRegistration(id: string, reason: string): Promise<RegistrationRequest> {
  return apiPost<RegistrationRequest>(`/registration-requests/${id}/reject`, { reason });
}
